/**
 * Eigene Emojis auflösen: zuerst aus den Servern im Store, sonst einmalig per `GET /emojis/:id` nachladen
 * (Emojis aus fremden Servern, z. B. in Reaktionen oder gespiegelten Nachrichten).
 */
import type { EmojiDto } from '@lagerfeuer/shared';
import { useEffect } from 'react';
import { create } from 'zustand';
import { expressionsApi } from '../../lib/endpoints';
import { useAppStore } from '../../store/app';
import type { AppData } from '../../store/types';

interface EmojiCacheState {
  /** null = nicht auffindbar (kein erneuter Versuch). */
  byId: Record<string, EmojiDto | null>;
}

export const useEmojiCache = create<EmojiCacheState>(() => ({ byId: {} }));
const inflight = new Set<string>();

export function requestEmoji(id: string): void {
  if (!/^\d{1,20}$/.test(id) || inflight.has(id) || id in useEmojiCache.getState().byId) return;
  inflight.add(id);
  expressionsApi
    .emoji(id)
    .then((e) => useEmojiCache.setState((s) => ({ byId: { ...s.byId, [id]: e } })))
    .catch(() => useEmojiCache.setState((s) => ({ byId: { ...s.byId, [id]: null } })))
    .finally(() => inflight.delete(id));
}

/** Alle Emojis aller Server (Reihenfolge der Serverleiste). */
export function allServerEmojis(s: Pick<AppData, 'emojis' | 'serverOrder'>): EmojiDto[] {
  const out: EmojiDto[] = [];
  const seen = new Set<string>();
  for (const sid of [...s.serverOrder, ...Object.keys(s.emojis)]) {
    if (seen.has(sid)) continue;
    seen.add(sid);
    out.push(...(s.emojis[sid] ?? []));
  }
  return out;
}

export function findEmoji(s: Pick<AppData, 'emojis'>, id: string): EmojiDto | undefined {
  for (const list of Object.values(s.emojis)) {
    const e = list.find((x) => x.id === id);
    if (e) return e;
  }
  return undefined;
}

/** URL eines eigenen Emojis (Store oder Cache), lädt bei Bedarf nach. */
export function useEmojiUrl(id: string | null | undefined): string | null {
  const fromStore = useAppStore((s) => (id ? findEmoji(s, id)?.url : undefined));
  const fromCache = useEmojiCache((s) => (id ? s.byId[id]?.url : undefined));
  useEffect(() => {
    if (id && !fromStore && fromCache === undefined) requestEmoji(id);
  }, [id, fromStore, fromCache]);
  return fromStore ?? fromCache ?? null;
}
